import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import Table from "@via/components/table/table";
import IndeterminateCheckbox from "@via/components/inputs/indeterminate-checkbox";
import AdvancedFilter from "@via/components/advanced-filter/advanced-filter";
import { SelectOption, OperatorMap } from "@via/utils/interfaces";
import { useVmetaProjects } from "@via/pages/projects/projects.api";
import { usePopulatedVmetaData } from "@via/pages/projects/metadata/metadata.api";
import { useDebounceCallback } from "usehooks-ts";
import { createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState, useEffect, useCallback } from "react";
import { VmetaProject, AddExistingFilesVmetaDataSetRequestBody } from "common";
import ColumnSelectorButton from "@via/components/column-selector-button/column-selector-button";
import {
  FilterItem,
  mapOperatorToMongo,
  renderCell,
  HIDE_COLUMNS,
} from "@via/pages/data/premium-dataset/utils/helper";
import { FieldType } from "@via/components/advanced-filter/helpers/types";
import { getFieldTypeFromString } from "@via/components/advanced-filter/helpers/utils";

interface AddExistingFilesModalProps {
  isOpen: boolean;
  datasetId: string;
  onClose: () => void;
  onAddFiles: (
    canvasId: string,
    payload: AddExistingFilesVmetaDataSetRequestBody
  ) => Promise<void>;
}

const columnHelper = createColumnHelper<Record<string, unknown>>();
const canvasPageSize = 10;

export default function AddExistingFilesModal({
  isOpen,
  onClose,
  onAddFiles,
}: AddExistingFilesModalProps) {
  const [selectedCanvas, setSelectedCanvas] = useState<SelectOption | null>(
    null
  );
  const [canvasSearchInput, setCanvasSearchInput] = useState("");
  const [canvasPage, setCanvasPage] = useState(0);
  const [cumulativeCanvases, setCumulativeCanvases] = useState<VmetaProject[]>(
    []
  );

  // File selection state
  const [selectedFiles, setSelectedFiles] = useState<Record<string, unknown>[]>(
    []
  );
  const [filePage, setFilePage] = useState(0);
  const [filePageSize, setFilePageSize] = useState(10);
  const [fileSort, setFileSort] = useState<
    Array<{ id: string; desc: boolean }>
  >([]);

  const [isAllFilesSelected, setIsAllFilesSelected] = useState(false);
  const [totalFilesCount, setTotalFilesCount] = useState(0);
  const [deselectedFileIds, setDeselectedFileIds] = useState<Set<string>>(
    new Set()
  );
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  // Filter state - using extended FilterItem with operator
  const [filters, setFilters] = useState<
    (FilterItem & { operator?: string })[]
  >([]);
  const [filterLogic, setFilterLogic] = useState<"AND" | "OR">("OR");

  const debouncedHandleCanvasInputChange = useDebounceCallback(
    (value: string) => {
      if (value === canvasSearchInput) return;
      setCanvasPage(0);
      setCanvasSearchInput(value);
      setCumulativeCanvases([]); // Reset cumulative data when searching
    },
    300
  );

  const { data: canvasData, isLoading: isCanvasLoading } = useVmetaProjects(
    canvasPage,
    canvasPageSize,
    [],
    canvasSearchInput ? [{ id: "name", value: canvasSearchInput }] : [],
    isOpen
  );

  useEffect(() => {
    if (!canvasData) return;

    setCumulativeCanvases(prevCanvases => {
      const newData = canvasData.data ?? [];

      if (!newData.length) return prevCanvases;

      // Append new results while preventing duplicates
      const existingIds = new Set(prevCanvases.map(c => c._id.toString()));
      const newCanvases = newData.filter(
        c => !existingIds.has(c._id.toString())
      );

      return [...prevCanvases, ...newCanvases];
    });
  }, [canvasData?.data, canvasPage, canvasSearchInput]);

  const operatorMap: OperatorMap = useMemo(() => {
    const map: OperatorMap = {};
    filters.forEach(filter => {
      if (filter.operator) {
        // Map field name to the MongoDB operator
        map[filter.id] = mapOperatorToMongo(filter.operator);
      }
    });
    return map;
  }, [filters]);

  // Fetch files when canvas is selected
  const { data: fileData, isLoading: isFileLoading } = usePopulatedVmetaData(
    (selectedCanvas?.value as string) || "",
    "file",
    {
      take: filePageSize,
      skip: filePage * filePageSize,
      filter:
        filters.length > 0
          ? filterLogic === "AND"
            ? { and: filters }
            : { or: filters }
          : undefined,
      operatorMap,
      sort: fileSort,
    }
  );

  useEffect(() => {
    if (fileData?.results) {
      setTotalFilesCount(fileData.results);
    }
  }, [fileData?.results]);

  // Convert field types to AdvancedFilter format
  const fieldTypes: FieldType[] = useMemo(() => {
    if (!fileData?.meta?.field_types) return [];

    return fileData.meta.field_types.map(field => ({
      name: field.name as string,
      label: field.label as string,
      type: getFieldTypeFromString(field.type as string),
      hasNamingPattern: field.hasNamingPattern as boolean,
    }));
  }, [fileData?.meta?.field_types]);

  const canvasOptions = useMemo(() => {
    return (
      cumulativeCanvases?.map((canvas: VmetaProject) => ({
        label: canvas.label,
        value: canvas._id.toString(),
      })) || []
    );
  }, [cumulativeCanvases]);

  const columns = useMemo(() => {
    if (!fileData) return [];

    const checkboxColumn = columnHelper.accessor("checkboxColumn", {
      header: () => (
        <IndeterminateCheckbox
          checked={isAllFilesSelected}
          indeterminate={
            !isAllFilesSelected &&
            selectedFiles.length > 0 &&
            selectedFiles.length < totalFilesCount
          }
          onChange={toggleAllFilesSelected}
          onClick={e => e.stopPropagation()}
          className="text-indigo-600"
        />
      ),
      cell: ({ row }) => (
        <IndeterminateCheckbox
          onClick={e => e.stopPropagation()}
          checked={
            (isAllFilesSelected &&
              !deselectedFileIds.has(row.original._id as string)) ||
            selectedFiles.some(({ _id }) => _id === row.original._id)
          }
          onChange={() => handleFileToggle(row.original)}
          className="text-indigo-600"
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
      size: 50,
    });

    const dataColumns =
      fileData?.meta?.field_types?.map(field => {
        return columnHelper.accessor(field?.name as string, {
          cell: info => <span>{renderCell(info, field) as string}</span>,
          header: () => <span>{field?.label as string}</span>,
          size: 200,
        });
      }) || [];

    return [checkboxColumn, ...dataColumns];
  }, [
    fileData,
    selectedFiles,
    isAllFilesSelected,
    deselectedFileIds,
    totalFilesCount,
  ]);

  const columnSelectorOptions = useMemo(
    () =>
      fieldTypes.map(field => ({
        id: field.name,
        label: field.label,
      })),
    [fieldTypes]
  );

  useEffect(() => {
    if (!fieldTypes.length) {
      setColumnVisibility({});
      return;
    }

    setColumnVisibility(prev => {
      const updated: Record<string, boolean> = {};
      fieldTypes.forEach(field => {
        const fieldName = field.name;
        const defaultVisible = !HIDE_COLUMNS.includes(fieldName);
        updated[fieldName] =
          prev[fieldName] !== undefined ? prev[fieldName] : defaultVisible;
      });

      const prevKeys = Object.keys(prev);
      const updatedKeys = Object.keys(updated);
      const hasChanges =
        updatedKeys.length !== prevKeys.length ||
        updatedKeys.some(key => updated[key] !== prev[key]) ||
        prevKeys.some(key => !(key in updated));

      return hasChanges ? updated : prev;
    });
  }, [fieldTypes]);

  const handleColumnSelectorToggle = useCallback(
    (columnId: string, nextValue: boolean) => {
      setColumnVisibility(prev => ({
        ...prev,
        [columnId]: nextValue,
      }));
    },
    []
  );

  const tableColumnVisibility = useMemo(
    () => ({
      checkboxColumn: true,
      ...columnVisibility,
    }),
    [columnVisibility]
  );

  const toggleAllFilesSelected = useCallback(() => {
    if (isAllFilesSelected) {
      setSelectedFiles([]);
      setIsAllFilesSelected(false);
      setDeselectedFileIds(new Set());
    } else {
      // Select all files - set virtual selection state
      // We don't need to fetch all files, just mark that all are selected
      setIsAllFilesSelected(true);
      setDeselectedFileIds(new Set()); // Clear any previous deselections

      // Select all files on current page for immediate visual feedback
      const currentPageFiles = fileData?.data || [];
      setSelectedFiles(currentPageFiles);
    }
  }, [isAllFilesSelected, fileData?.data]);

  const handleFileToggle = useCallback(
    (file: Record<string, unknown>) => {
      const fileId = file._id as string;
      const isCurrentlySelected = isAllFilesSelected
        ? !deselectedFileIds.has(fileId)
        : selectedFiles.some(item => item._id === file._id);

      if (isCurrentlySelected) {
        // Deselecting a file
        if (isAllFilesSelected) {
          // Add to deselected set
          setDeselectedFileIds(prev => new Set([...prev, fileId]));
          // Remove from selectedFiles if it exists there
          setSelectedFiles(prev => prev.filter(item => item._id !== file._id));
        } else {
          // Normal deselection
          setSelectedFiles(prev => prev.filter(item => item._id !== file._id));
        }
      } else {
        // Selecting a file
        if (isAllFilesSelected) {
          // Remove from deselected set (if it was there)
          setDeselectedFileIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(fileId);
            return newSet;
          });
        } else {
          // Normal selection
          setSelectedFiles(prev => [...prev, file]);
        }
      }
    },
    [selectedFiles, isAllFilesSelected, deselectedFileIds]
  );

  // Handle filter changes
  const handleFiltersChange = useCallback(
    (
      newFilters: (FilterItem & { operator?: string })[],
      logic: "AND" | "OR" = "OR"
    ) => {
      setFilters(newFilters);
      setFilterLogic(logic);
      setFilePage(0); // Reset to first page when filters change
    },
    []
  );

  // Canvas selection handler
  const handleCanvasChange = useCallback((selected: any) => {
    setSelectedCanvas(selected);
    setSelectedFiles([]);
    setFilePage(0);
    setIsAllFilesSelected(false);
    setTotalFilesCount(0);
    setDeselectedFileIds(new Set());
    setFilters([]); // Reset filters when canvas changes
    setFilterLogic("OR"); // Reset filter logic when canvas changes
    setColumnVisibility({});
  }, []);

  // Fetch more canvases
  const fetchMoreCanvases = useCallback(() => {
    setCanvasPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage =
        Math.ceil((canvasData?.results ?? 0) / canvasPageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [canvasData?.results, canvasPageSize]);

  const handleAddFiles = useCallback(async () => {
    if (!selectedCanvas || (selectedFiles.length === 0 && !isAllFilesSelected))
      return;

    if (isAllFilesSelected) {
      // When all files are selected, send a special payload to the backend
      // The backend will handle getting all file IDs and excluding deselected ones
      const deselectedIdsArray = Array.from(deselectedFileIds);

      await onAddFiles(selectedCanvas.value as string, {
        selectAll: true,
        deselectedFileIds: deselectedIdsArray,
        canvasId: selectedCanvas.value as string,
      });
    } else {
      // Use currently selected files
      const fileIds = selectedFiles.map(file => file._id as any);

      await onAddFiles(selectedCanvas.value as string, {
        canvasId: selectedCanvas.value as string,
        fileIds,
      });
    }

    onClose();
  }, [
    selectedCanvas,
    selectedFiles,
    isAllFilesSelected,
    deselectedFileIds,
    fileSort,
    onAddFiles,
    onClose,
  ]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCanvas(null);
      setSelectedFiles([]);
      setCanvasSearchInput("");
      setCanvasPage(0);
      setFilePage(0);
      setCumulativeCanvases([]);
      setIsAllFilesSelected(false);
      setTotalFilesCount(0);
      setDeselectedFileIds(new Set());
      setFilters([]);
      setFilterLogic("OR");
      setColumnVisibility({});
    }
  }, [isOpen]);

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Link Existing Files"
      showButtons={false}
      width={960}>
      <div className="space-y-6">
        <div>
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Select Sample Tracker
          </label>
          <SelectComponent
            name="canvasSelection"
            options={canvasOptions}
            value={selectedCanvas}
            onChange={handleCanvasChange}
            isLoading={isCanvasLoading}
            placeholder="Choose sample tracker or type to create"
            enableClear={false}
            onFetchMoreOptions={fetchMoreCanvases}
            onInputChange={debouncedHandleCanvasInputChange}
          />
        </div>

        {selectedCanvas && (
          <div>
            {/* Advanced Filter Component */}
            {fieldTypes.length > 0 && totalFilesCount > 0 && (
              <div className="mb-4">
                <AdvancedFilter
                  fields={fieldTypes}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  operatorMap={operatorMap}
                  maxFilters={10}
                  filterLogic={filterLogic}
                  debugMode={false}
                  rightActions={
                    columnSelectorOptions.length > 0 ? (
                      <ColumnSelectorButton
                        columns={columnSelectorOptions}
                        columnVisibility={tableColumnVisibility}
                        onToggle={handleColumnSelectorToggle}
                      />
                    ) : null
                  }
                />
              </div>
            )}

            <div className="mb-4 flex items-center justify-between"></div>
            <div className="overflow-auto h-96">
              <Table
                isLoading={isFileLoading}
                data={fileData?.data || []}
                columns={columns}
                sort={fileSort}
                setSort={setFileSort}
                page={filePage}
                setPage={setFilePage}
                pageSize={filePageSize}
                setPageSize={setFilePageSize}
                total={fileData?.results || 0}
                emptyMessage="No files available in this canvas."
                customTableClassName="w-max whitespace-nowrap"
                columnVisibility={tableColumnVisibility}
              />
            </div>
          </div>
        )}

        {/* Selected files display */}
        {(selectedFiles.length > 0 || isAllFilesSelected) && (
          <div className="text-sm text-gray-600">
            {isAllFilesSelected
              ? `${totalFilesCount - deselectedFileIds.size} of ${totalFilesCount} files selected`
              : `${selectedFiles.length} of ${totalFilesCount} files selected`}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button
            onClick={handleAddFiles}
            disabled={
              !selectedCanvas ||
              (selectedFiles.length === 0 && !isAllFilesSelected)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            Add Files
          </button>
        </div>
      </div>
    </Modal>
  );
}
